import { useEffect, useState } from "react";
import {
  Box,
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
  CircularProgress,
} from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import Header from "../../../../components/Header";
import { AssetFinanceUrl } from "../../../../config";
import {
  executeDynamicSubmit,
  fetchJsonDynamic,
} from "../../../../data/DynamicServiceNew/jsondynamicservice";
import Swal from "sweetalert2";

const base_url = AssetFinanceUrl.uri;

const Form = (props) => {
  const decodedToken = JSON.parse(localStorage.getItem("decodedToken"));
  const [assetTypes, setAssetTypes] = useState([]);
  const [loadingTypes, setLoadingTypes] = useState(true);

  useEffect(() => {
    (async () => {
      const response = await fetchJsonDynamic("allAssetTypes", null, null, base_url);
      const results = response?.results ? JSON.parse(response.results) : [];
      setAssetTypes(results.filter((t) => t.IsActive !== false));
      setLoadingTypes(false);
    })();
  }, []);

  const initialValues = {
    assetTypeId: props.data ? props.data.AssetTypeId : "",
    code: props.data ? props.data.AssetSubTypeCode : "",
    displayName: props.data ? props.data.AssetSubTypeName : "",
    description: props.data ? props.data.AssetSubTypeDescription : "",
    defaultMaxLtvPct: props.data ? props.data.DefaultMaxLtvPct : "",
    requiresSpousalConsent: props.data ? !!props.data.RequiresSpousalConsent : false,
    displayOrder: props.data ? props.data.DisplayOrder : "",
    isActive: props.data ? !!props.data.IsActive : true,
  };

  const validationSchema = yup.object().shape({
    assetTypeId: yup.number().typeError("Asset type is required").required("Asset type is required"),
    code: yup.string().required("Code is required"),
    displayName: yup.string().required("Display name is required"),
    description: yup.string().nullable(),
    defaultMaxLtvPct: yup.number().typeError("Enter a valid percentage").nullable(),
    displayOrder: yup.number().typeError("Enter a valid number").nullable(),
  });

  const handleSubmit = async (values, { setSubmitting }) => {
    const assetSubType = {
      assetTypeId: Number(values.assetTypeId),
      code: values.code,
      displayName: values.displayName,
      description: values.description || "",
      defaultMaxLtvPct: values.defaultMaxLtvPct ? parseFloat(values.defaultMaxLtvPct) : null,
      indicativeDepreciationPct: 0,
      validityMonthsOverride: null,
      requiresCommercialLicence: false,
      requiresLcbConsent: false,
      requiresSpousalConsent: values.requiresSpousalConsent,
      displayOrder: values.displayOrder ? parseInt(values.displayOrder) : 0,
      companyId: parseInt(decodedToken.CompanyId),
      isActive: values.isActive,
    };

    if (props.isEditing) {
      assetSubType.id = props.data.Id;
    }

    await executeDynamicSubmit({
      mutationName: props.isEditing ? "updateAssetSubType" : "createAssetSubType",
      variables: assetSubType,
      mutationObjectName: "assetSubType",
      baseUrl: base_url,
      isEditing: props.isEditing,
      entityName: "Asset Sub-Type",
      onSuccess: () => {
        props.onClose();
        props.onAction();
      },
      setSubmitting,
    });
  };

  if (loadingTypes) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box m="20px">
      <Header
        title={props.isEditing ? "Edit Asset Sub-Type" : "Create Asset Sub-Type"}
        subtitle={
          props.isEditing
            ? "Edit an Existing Collateral Asset Sub-Type"
            : "Create a New Collateral Asset Sub-Type"
        }
      />

      <Formik
        onSubmit={handleSubmit}
        initialValues={initialValues}
        validationSchema={validationSchema}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          isSubmitting,
          setFieldValue,
        }) => (
          <form onSubmit={handleSubmit}>
            <Box
              display="grid"
              gap="30px"
              gridTemplateColumns="repeat(4, minmax(0, 1fr))"
            >
              <TextField
                select
                fullWidth
                variant="filled"
                label="Asset Type*"
                name="assetTypeId"
                value={values.assetTypeId}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.assetTypeId && !!errors.assetTypeId}
                helperText={touched.assetTypeId && errors.assetTypeId}
                SelectProps={{ native: true }}
                sx={{ gridColumn: "span 2" }}
              >
                <option value="" disabled></option>
                {assetTypes.map((t) => (
                  <option key={t.Id} value={t.Id}>
                    {t.DisplayName}
                  </option>
                ))}
              </TextField>
              <TextField
                fullWidth
                variant="filled"
                type="text"
                label="Code*"
                placeholder="e.g. SALOON"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.code}
                name="code"
                error={touched.code && !!errors.code}
                helperText={touched.code && errors.code}
                sx={{ gridColumn: "span 2" }}
              />
              <TextField
                fullWidth
                variant="filled"
                type="text"
                label="Display Name*"
                placeholder="e.g. Saloon car"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.displayName}
                name="displayName"
                error={touched.displayName && !!errors.displayName}
                helperText={touched.displayName && errors.displayName}
                sx={{ gridColumn: "span 4" }}
              />
              <TextField
                fullWidth
                multiline
                minRows={2}
                variant="filled"
                type="text"
                label="Description"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.description}
                name="description"
                error={touched.description && !!errors.description}
                helperText={touched.description && errors.description}
                sx={{ gridColumn: "span 4" }}
              />
              <TextField
                fullWidth
                variant="filled"
                type="number"
                label="Default Max LTV (%)"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.defaultMaxLtvPct}
                name="defaultMaxLtvPct"
                error={touched.defaultMaxLtvPct && !!errors.defaultMaxLtvPct}
                helperText={touched.defaultMaxLtvPct && errors.defaultMaxLtvPct}
                sx={{ gridColumn: "span 2" }}
              />
              <TextField
                fullWidth
                variant="filled"
                type="number"
                label="Display Order"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.displayOrder}
                name="displayOrder"
                error={touched.displayOrder && !!errors.displayOrder}
                helperText={touched.displayOrder && errors.displayOrder}
                sx={{ gridColumn: "span 2" }}
              />
              <Box sx={{ gridColumn: "span 4", display: "flex", flexWrap: "wrap", gap: "10px" }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={values.requiresSpousalConsent}
                      onChange={(e) => setFieldValue("requiresSpousalConsent", e.target.checked)}
                    />
                  }
                  label="Requires Spousal Consent"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={values.isActive}
                      onChange={(e) => setFieldValue("isActive", e.target.checked)}
                    />
                  }
                  label="Active"
                />
              </Box>
            </Box>
            <Box display="flex" justifyContent="end" mt="20px">
              <Button
                type="submit"
                color="primary"
                variant="contained"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Submitting..." : "Submit"}
              </Button>
            </Box>
          </form>
        )}
      </Formik>
    </Box>
  );
};

export default Form;
